import { Form, Input, Button, Checkbox, Card, Typography } from 'antd';
import { useTranslation } from "react-i18next";
import { useUser } from "../../core/context/UserContext";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Flex from "../../core/components/Flex";
import ApiService from "../../core/service/ApiService";
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { Branding } from "../../core/service/BrandingService";
import ForgotPasswordForm from "./ForgotPasswordForm";
import "./login.css";
// import ReCAPTCHA from 'react-google-recaptcha';

const { Title, Text } = Typography;

export default function LoginApp() {
    const { t } = useTranslation();
    const formRef = useRef();
    const { user, setToken, isAuth } = useUser();
    const history = useHistory();
    const [error, setError] = useState('')
    const loginLogoBackground = Branding.logoBackground('loginLogo');
    const [forgot, setForgot] = useState(false)
    const [authParams, setAuthParams] = useState({
        enabled: false,
        key: null,
        timeout: 0,
        sms_auth: false,
        mail_auth: false,
        sms_text: '',
        mail_text: '',
    });

    const sendCode = async (type) => {
        setError('')

        const res = await ApiService.Auth.SendCode({
            code_key: authParams.key,
            code_type: type,
            grant_type: 'password'
        })

        if (res.success) {
            setAuthParams({
                ...authParams,
                enabled: true,
                sent: true,                 // kod alanı sayaçtan BAĞIMSIZ görünür (sunucudaki kod ömrü 3 dk)
                key: res.data?.access_token,
                timeout: 60,                // yalnız "tekrar gönder" bekleme süresi
                sms_auth: authParams.sms_auth,
                mail_auth: authParams.mail_auth,
            })
            startTimer()
        }
    }

    const startTimer = () => {
        const interval = setInterval(() => {
            setAuthParams((prev) => {
                const nextTimeout = prev.timeout - 1
                if (nextTimeout === 0) {
                    clearInterval(interval)
                }

                return {
                    ...prev,
                    timeout: prev.timeout - 1
                }
            })
        }, 1000)
    }

    const signIn = async (form) => {
        if (!authParams.enabled) {
            const response = await ApiService.Auth.Login({
                ...form,
                grant_type: 'password'
            })
            if (response.success) {
                if (response.data.sms_auth || response.data.mail_auth) {
                    setAuthParams({
                        enabled: true,
                        sms_auth: response.data.sms_auth,
                        mail_auth: response.data.mail_auth,
                        timeout: 0,
                        key: response.data?.access_token,
                        sms_text: response.data.sms_text,
                        mail_text: response.data.mail_text,
                    })
                }
                else {
                    setToken(response.data?.access_token)
                }
            }
            else {
                setError('Username or password is incorrect!')
            }
        }
        else {
            const verifyResponse = await ApiService.Auth.SmsVerify({
                password: form.code,
                code_key: authParams.key
            })

            if (verifyResponse.success) {
                setToken(verifyResponse.data?.access_token)
            }
            else {
                setError(t('Verification code is wrong!'))
            }
        }
    }

    const redirectToHome = () => {
        if (isAuth()) {
            history.push(`/`)
        }
    }

    useEffect(() => {
        redirectToHome()
    }, [])

    useEffect(() => {
        redirectToHome()
    }, [user])

    return (
        <div style={{
            height: '100vh', // Ensure full viewport height
            width: '100%',
            backgroundColor: 'var(--bg)',
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${Branding.asset('loginBackground')})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
            <div style={{
                width: '100%',
                maxWidth: 400,
                padding: '0 20px' // Mobile gutter
            }}>
                <Card
                    bordered={false}
                    style={{
                        borderRadius: 16,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', // Slight transparency
                        backdropFilter: 'blur(10px)',
                    }}
                    bodyStyle={{ padding: '40px 32px' }}
                >
                    <div style={{ textAlign: 'center', marginBottom: 32 }}>
                        {/* kiracının logosu (yoksa bizim varsayılanımız); şeffaf değilse marka ayarındaki renk kutusunda */}
                        <div className="login-logo">
                            <span
                                className={`login-logo-box ${loginLogoBackground ? 'has-bg' : ''}`}
                                style={loginLogoBackground ? { background: loginLogoBackground } : undefined}
                            >
                                <img src={Branding.asset('loginLogo')} alt={Branding.displayName() || 'Logo'} />
                            </span>
                        </div>
                        <Title level={3} style={{ marginBottom: 4, color: '#333' }}>
                            {t('Welcome Back')}
                        </Title>
                        <Text type="secondary">
                            {t('Please sign in to continue')}
                        </Text>
                    </div>

                    {forgot ? (
                        <ForgotPasswordForm onBack={() => setForgot(false)} />
                    ) : (
                    <Form
                        name="basic"
                        layout={'vertical'}
                        initialValues={{ remember: true }}
                        autoComplete="off"
                        onFinish={signIn}
                        size="large"
                    >
                        <Form.Item
                            name="username"
                            rules={[{ required: true, message: t('Please input your username!') }]}
                        >
                            <Input
                                prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                placeholder={t('Username')}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>

                        {
                            !authParams.enabled && (
                                <Form.Item
                                    name="password"
                                    rules={[{ required: true, message: t('Please input your password!') }]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                        placeholder={t('Password')}
                                        disabled={authParams.enabled}
                                        style={{ borderRadius: 8 }}
                                    />
                                </Form.Item>
                            )
                        }

                        {
                            authParams.enabled && (
                                <>
                                    <Form.Item style={{ marginBottom: 12 }}>
                                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                                            <Text>{t('You need to verify the security code to log in to your account.')}</Text>
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {
                                                authParams.sms_auth && (
                                                    <Button
                                                        block
                                                        disabled={authParams.timeout > 0}
                                                        onClick={() => sendCode('sms')}
                                                        style={{ borderRadius: 8 }}
                                                    >
                                                        {t('SMS')} {` (*** *** **${authParams.sms_text})`}
                                                    </Button>
                                                )
                                            }
                                            {
                                                authParams.mail_auth && (
                                                    <Button
                                                        block
                                                        disabled={authParams.timeout > 0}
                                                        onClick={() => sendCode('mail')}
                                                        style={{ borderRadius: 8 }}
                                                    >
                                                        {t('Mail')} {` (${authParams.mail_text})`}
                                                    </Button>
                                                )
                                            }
                                        </div>
                                    </Form.Item>

                                    {
                                        authParams.sent && (
                                            <Form.Item
                                                name="code"
                                                rules={[{ required: true, message: t('Please input sms verification code!') }]}
                                            >
                                                <Input
                                                    placeholder={t('Verification Code')}
                                                    addonAfter={authParams.timeout > 0 ? ` (${authParams.timeout})` : null}
                                                    style={{ borderRadius: 8 }}
                                                />
                                            </Form.Item>
                                        )
                                    }
                                </>
                            )
                        }

                        {error && (
                            <Form.Item style={{ marginBottom: 12 }}>
                                <div style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#fff2f0',
                                    border: '1px solid #ffccc7',
                                    borderRadius: 6,
                                    textAlign: 'center'
                                }}>
                                    <Text type="danger">{t(error)}</Text>
                                </div>
                            </Form.Item>
                        )}

                        {
                            ((authParams.enabled && authParams.timeout > 0) || !authParams.enabled) && (
                                <Form.Item style={{ marginBottom: 0, marginTop: 12 }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        block
                                        style={{ height: 45, borderRadius: 8, fontSize: 16, fontWeight: 500 }}
                                    >
                                        {!authParams.enabled ? t('Sign In') : t('Verify & Login')}
                                    </Button>
                                </Form.Item>
                            )
                        }

                        {/* 2FA adımına geçildikten sonra gizlenir: kullanıcı zaten parolasını doğru girmiş. */}
                        {
                            !authParams.enabled && (
                                <div style={{ textAlign: 'center', marginTop: 8 }}>
                                    <Button type="link" onClick={() => setForgot(true)}>
                                        {t('Forgot my password')}
                                    </Button>
                                </div>
                            )
                        }
                    </Form>
                    )}

                    {/* Üretici imzası — beyaz etiket ayarıyla KALDIRILAMAZ (kodda koşulsuz). */}
                    <div className="login-company">
                        <a
                            className="login-company-row"
                            href="https://kuantumbulut.com"
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            <img src="/logo-his-light.svg" alt="HIS · eCloudPACS" className="login-company-product" />
                            <span className="login-company-site">kuantumbulut.com</span>
                        </a>
                        <div className="login-company-copy">
                            © {new Date().getFullYear()} Kuantum Bulut Bilişim ve Yazılım A.Ş.
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    )
}
